import { ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useFacilitiesByUserId } from "@/modules/facility/facilityHooks";
import { getUserIdFromToken } from "@/modules/user/userHooks";

interface DashboardToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  facilityId: string | undefined;
  onFacilityChange: (facilityId: string | undefined) => void;
}

export function DashboardToolbar({
  search,
  onSearchChange,
  facilityId,
  onFacilityChange,
}: DashboardToolbarProps) {
  const [facilities] = useFacilitiesByUserId(getUserIdFromToken());
  const selectedFacilityName = facilities.find((f) => f.id === facilityId)?.name;
  const facilityLabel = selectedFacilityName ?? "All";

  return (
    <div className="flex items-center gap-3 max-sm:w-full">
      {/* The field's chrome lives on the wrapper so the icon is a flex child
          rather than an absolutely-positioned overlay: it centres with the
          row instead of against a box whose height it cannot see, and the
          focus ring wraps the icon and the input together. */}
      <div className="flex h-9 items-center gap-2 rounded-xl border border-border bg-surface px-3 focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30 max-sm:flex-1 sm:w-72">
        <Search className="size-3.5 shrink-0 text-text-muted" />
        <Input
          placeholder="Search leagues"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-full min-w-0 flex-1 rounded-none border-0 bg-transparent p-0 text-xs shadow-none focus-visible:border-0 focus-visible:ring-0"
        />
      </div>
      {facilities.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="outline"
                className="rounded-lg bg-surface text-xs font-normal dark:bg-surface"
                // The label is two spans with no whitespace between them, so
                // the computed name would otherwise run together as
                // "FacilityAll".
                aria-label={`Facility ${facilityLabel}`}
              />
            }
          >
            <span className="text-text-muted">Facility</span>
            <span className="font-bold text-ink">{facilityLabel}</span>
            <ChevronDown className="size-3.5 text-text-muted" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onFacilityChange(undefined)}>
              All
            </DropdownMenuItem>
            {facilities.map((f) => (
              <DropdownMenuItem key={f.id} onClick={() => onFacilityChange(f.id)}>
                {f.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
